import { useState, useEffect } from 'react'
import { X, Save, User, Eye, EyeOff, Camera, MapPin, Search, Loader2 } from 'lucide-react'
import api from '../services/api'

const UserModal = ({ isOpen, onClose, user = null, onSuccess, currentUser = null }) => {
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        password: '',
        roleId: '',
        isActive: true
    })
    const [roles, setRoles] = useState([])
    const [loading, setLoading] = useState(false)
    const [errors, setErrors] = useState({})
    const [showPassword, setShowPassword] = useState(false)
    
    // Camera assignment state
    const [cameras, setCameras] = useState([])
    const [selectedCameras, setSelectedCameras] = useState([])
    const [selectedLocations, setSelectedLocations] = useState([])
    const [cameraAssignmentTab, setCameraAssignmentTab] = useState('individual') // 'individual' or 'location'
    const [cameraSearchTerm, setCameraSearchTerm] = useState('')
    const [loadingCameras, setLoadingCameras] = useState(false)
    const [savingCameras, setSavingCameras] = useState(false)
    const [showCameraAssignment, setShowCameraAssignment] = useState(false)
    const [cameraAssignmentsModified, setCameraAssignmentsModified] = useState(false)

    const isEditing = !!user
    
    // Check if the user being edited is the current user (for role change prevention)
    const isEditingSelf = currentUser && user && 
        currentUser.id && user._id &&
        currentUser.id.toString() === user._id.toString()
    

    useEffect(() => {
        if (isOpen) {
            fetchRoles()
            if (isEditing) {
                fetchCamerasAndAssignments()
            }
            if (user) {
                setFormData({
                    username: user.username,
                    email: user.email,
                    password: '',
                    roleId: user.role?._id || '',
                    isActive: user.isActive
                })
            } else {
                setFormData({
                    username: '',
                    email: '',
                    password: '',
                    roleId: '',
                    isActive: true
                })
                setSelectedCameras([])
                setSelectedLocations([])
            }
            setErrors({})
            // Auto-expand camera assignment when editing
            setShowCameraAssignment(isEditing)
            setCameraSearchTerm('')
            setCameraAssignmentsModified(false)
        }
    }, [isOpen, user, isEditing])

    const fetchRoles = async () => {
        try {
            const response = await api.get('/roles')
            setRoles(response.data.data.roles)
        } catch (error) {
            console.error('Error fetching roles:', error)
        }
    }

    const fetchCamerasAndAssignments = async () => {
        if (!user?._id) return
        
        try {
            setLoadingCameras(true)
            const response = await api.get('/cameras?limit=10000')
            const allCameras = response.data.data.cameras || []
            setCameras(allCameras)
            
            // Find cameras where this user is in accessUsers
            // Handle both cases: accessUsers as ObjectId objects or as strings
            const userId = user._id.toString()
            const assignedCameras = allCameras
                .filter(camera => {
                    if (!camera.accessUsers || camera.accessUsers.length === 0) return false
                    return camera.accessUsers.some(accessUser => {
                        // Handle both populated objects and plain IDs
                        const accessUserId = accessUser._id ? accessUser._id.toString() : accessUser.toString()
                        return accessUserId === userId
                    })
                })
                .map(camera => camera._id)
            
            console.log('Assigned cameras for user:', assignedCameras.length, assignedCameras)
            setSelectedCameras(assignedCameras)
            
            // Extract unique locations from assigned cameras
            const assignedLocations = new Set()
            allCameras
                .filter(camera => assignedCameras.includes(camera._id))
                .forEach(camera => {
                    if (camera.location?.exactLocation && camera.location.exactLocation !== 'general') {
                        assignedLocations.add(camera.location.exactLocation)
                    }
                })
            
            setSelectedLocations(Array.from(assignedLocations))
        } catch (error) {
            console.error('Error fetching cameras and assignments:', error)
        } finally {
            setLoadingCameras(false)
        }
    }

    const handleCameraToggle = (cameraId) => {
        setSelectedCameras(prev => {
            const newSelection = prev.includes(cameraId)
                ? prev.filter(id => id !== cameraId)
                : [...prev, cameraId]
            setCameraAssignmentsModified(true)
            return newSelection
        })
    }

    const handleLocationToggle = (location) => {
        setSelectedLocations(prev => {
            const isSelected = prev.includes(location)
            const newLocations = isSelected
                ? prev.filter(loc => loc !== location)
                : [...prev, location]
            
            // Update selected cameras based on location selection
            const camerasAtLocation = cameras
                .filter(cam => cam.location?.exactLocation === location)
                .map(cam => cam._id)
            
            if (isSelected) {
                // Remove cameras at this location
                setSelectedCameras(prevCams => {
                    const newCams = prevCams.filter(camId => !camerasAtLocation.includes(camId))
                    setCameraAssignmentsModified(true)
                    return newCams
                })
            } else {
                // Add cameras at this location
                setSelectedCameras(prevCams => {
                    const newCams = [...prevCams]
                    camerasAtLocation.forEach(camId => {
                        if (!newCams.includes(camId)) {
                            newCams.push(camId)
                        }
                    })
                    setCameraAssignmentsModified(true)
                    return newCams
                })
            }
            
            return newLocations
        })
    }

    const saveCameraAssignments = async () => {
        if (!user?._id) return
        
        try {
            setSavingCameras(true)
            
            // Get all cameras
            const allCameras = cameras
            const userId = user._id.toString()
            
            // Find cameras that need updates
            const camerasToUpdate = allCameras.filter(camera => {
                // Get current accessUsers - extract _id from populated user objects (same as CameraAssignmentModal)
                const currentAccessUsers = (camera.accessUsers || []).map(user => 
                    user._id ? user._id.toString() : user.toString()
                )
                
                const isSelected = selectedCameras.includes(camera._id)
                const isCurrentlyAssigned = currentAccessUsers.includes(userId)
                
                // Return true if assignment status changed
                return isSelected !== isCurrentlyAssigned
            })
            
            if (camerasToUpdate.length === 0) {
                console.log('No camera assignments to update')
                return
            }
            
            console.log(`Updating ${camerasToUpdate.length} camera(s)...`)
            
            // Update each camera that needs changes
            const updatePromises = camerasToUpdate.map(async (camera) => {
                // Get current accessUsers - extract _id from populated user objects
                const currentAccessUsers = (camera.accessUsers || []).map(user => 
                    user._id ? user._id.toString() : user.toString()
                )
                
                const isSelected = selectedCameras.includes(camera._id)
                
                let newAccessUsers
                if (isSelected) {
                    // Add user - keep existing IDs and add new one
                    newAccessUsers = [...currentAccessUsers, userId]
                } else {
                    // Remove user - keep all other users
                    newAccessUsers = currentAccessUsers.filter(id => id !== userId)
                }
                
                // Send the array of user IDs (same format as CameraAssignmentModal)
                console.log(`Updating camera ${camera.name}: accessUsers =`, newAccessUsers)
                return api.put(`/cameras/${camera._id}`, {
                    accessUsers: newAccessUsers
                })
            })
            
            await Promise.all(updatePromises)
            console.log('Camera assignments saved successfully')
        } catch (error) {
            console.error('Error saving camera assignments:', error)
            console.error('Error details:', error.response?.data)
            const errorMessage = error.response?.data?.message || error.message || 'Unknown error occurred'
            alert(`Failed to save camera assignments: ${errorMessage}`)
            throw error // Re-throw to prevent form submission from continuing
        } finally {
            setSavingCameras(false)
        }
    }

    const validateForm = () => {
        const newErrors = {}

        if (!formData.username.trim()) {
            newErrors.username = 'Username is required'
        } else if (formData.username.length < 3) {
            newErrors.username = 'Username must be at least 3 characters'
        }

        if (!formData.email.trim()) {
            newErrors.email = 'Email is required'
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Email is invalid'
        }

        if (!isEditing && !formData.password.trim()) {
            newErrors.password = 'Password is required'
        } else if (formData.password && formData.password.length < 6) {
            newErrors.password = 'Password must be at least 6 characters'
        }

        // Only require roleId for new users, not when editing existing users
        if (!isEditing && !formData.roleId) {
            newErrors.roleId = 'Role is required'
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = async (e) => {
        e.preventDefault()

        console.log('UserModal handleSubmit called')
        console.log('Form data:', formData)
        console.log('Is editing:', isEditing)
        console.log('User ID:', user?._id)

        if (!validateForm()) {
            console.log('Form validation failed')
            return
        }

        setLoading(true)
        try {
            const submitData = { ...formData }
            if (!submitData.password) {
                delete submitData.password
            }
            
            // Don't send roleId when editing self (to prevent role change attempts)
            if (isEditingSelf) {
                delete submitData.roleId
            }

            console.log('Submitting data:', submitData)

            if (isEditing) {
                console.log('Making PUT request to:', `/users/${user._id}`)
                
                // If password is provided, reset it separately
                if (submitData.password) {
                    console.log('Resetting password for user:', user._id)
                    await api.patch(`/users/${user._id}/reset-password`, {
                        newPassword: submitData.password
                    })
                    console.log('Password reset successful')
                }
                
                // Remove password from main update data
                delete submitData.password
                
                // Update other user data
                const response = await api.put(`/users/${user._id}`, submitData)
                console.log('Update response:', response)
            } else {
                console.log('Making POST request to:', '/users')
                const response = await api.post('/users', submitData)
                console.log('Create response:', response)
            }

            console.log('User saved successfully')
            
            // If editing and camera assignments were modified, save them
            if (isEditing && cameraAssignmentsModified) {
                try {
                    await saveCameraAssignments()
                } catch (error) {
                    // Error already shown in saveCameraAssignments
                    // Don't close modal if camera assignment failed
                    return
                }
            }
            
            onSuccess()
            onClose()
        } catch (error) {
            console.error('Error saving user:', error)
            console.error('Error response:', error.response?.data)
            console.error('Error status:', error.response?.status)
            
            if (error.response?.data?.errors) {
                setErrors(error.response.data.errors)
            } else if (error.response?.data?.message) {
                alert(`Error: ${error.response.data.message}`)
            } else {
                alert('An error occurred while saving the user')
            }
        } finally {
            setLoading(false)
        }
    }

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }))

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }))
        }
    }

    // Get unique locations from cameras
    const locationOptions = Array.from(
        new Set(
            cameras
                .filter(cam => cam.location?.exactLocation && cam.location.exactLocation !== 'general')
                .map(cam => cam.location.exactLocation)
        )
    ).sort()

    // Filter cameras based on search term
    const filteredCameras = cameras.filter(camera =>
        camera.name?.toLowerCase().includes(cameraSearchTerm.toLowerCase()) ||
        camera.location?.exactLocation?.toLowerCase().includes(cameraSearchTerm.toLowerCase())
    )

    // Get cameras grouped by location
    const camerasByLocation = locationOptions.reduce((acc, location) => {
        acc[location] = cameras.filter(cam => cam.location?.exactLocation === location)
        return acc
    }, {})

    // Sync selectedLocations with selectedCameras (only when cameras are loaded)
    useEffect(() => {
        if (!isEditing || cameras.length === 0 || loadingCameras) return
        
        const newSelectedLocations = new Set()
        locationOptions.forEach(location => {
            const camerasAtLocation = camerasByLocation[location] || []
            if (camerasAtLocation.length > 0) {
                const allSelected = camerasAtLocation.every(cam => selectedCameras.includes(cam._id))
                if (allSelected) {
                    newSelectedLocations.add(location)
                }
            }
        })
        
        const newLocationsArray = Array.from(newSelectedLocations).sort()
        const currentLocationsArray = selectedLocations.sort()
        
        // Only update if different to avoid unnecessary re-renders
        if (JSON.stringify(newLocationsArray) !== JSON.stringify(currentLocationsArray)) {
            setSelectedLocations(newLocationsArray)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCameras, cameras.length, isEditing, loadingCameras])

    if (!isOpen) return null

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className={`bg-white rounded-lg shadow-xl w-full ${showCameraAssignment ? 'max-w-4xl' : 'max-w-md'} mx-auto my-auto max-h-[95vh] flex flex-col`}>
                <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
                    <div className="flex items-center space-x-3">
                        <User className="h-5 w-5 text-primary-600" />
                        <h2 className="text-lg font-semibold text-gray-900">
                            {isEditing ? 'Edit User' : 'Add User'}
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1 min-h-0">
                    {/* Username */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Username *
                        </label>
                        <input
                            type="text"
                            name="username"
                            value={formData.username}
                            onChange={handleChange}
                            className={`input ${errors.username ? 'border-red-500' : ''}`}
                            placeholder="Enter username"
                        />
                        {errors.username && (
                            <p className="text-red-500 text-sm mt-1">{errors.username}</p>
                        )}
                    </div>

                    {/* Email */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Email *
                        </label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            className={`input ${errors.email ? 'border-red-500' : ''}`}
                            placeholder="Enter email"
                        />
                        {errors.email && (
                            <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                        )}
                    </div>

                    {/* Password */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Password {!isEditing && '*'}
                        </label>
                        <div className="relative">
                            <input
                                type={showPassword ? "text" : "password"}
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                className={`input pr-10 ${errors.password ? 'border-red-500' : ''}`}
                                placeholder={isEditing ? "Enter new password to reset" : "Enter password"}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                            >
                                {showPassword ? (
                                    <EyeOff className="h-4 w-4" />
                                ) : (
                                    <Eye className="h-4 w-4" />
                                )}
                            </button>
                        </div>
                        {errors.password && (
                            <p className="text-red-500 text-sm mt-1">{errors.password}</p>
                        )}
                        {isEditing && (
                            <div className="text-gray-500 text-sm mt-1">
                                <p>Enter new password to reset user's password (admin function)</p>
                                {isEditingSelf && (
                                    <p className="text-amber-600 mt-1">
                                        <strong>Note:</strong> To change your own password, use the Profile tab instead.
                                    </p>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Role */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Role *
                        </label>
                        <select
                            name="roleId"
                            value={formData.roleId}
                            onChange={handleChange}
                            disabled={isEditingSelf}
                            className={`input ${errors.roleId ? 'border-red-500' : ''} ${isEditingSelf ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                        >
                            <option value="">Select a role</option>
                            {roles.map((role) => (
                                <option key={role._id} value={role._id}>
                                    {role.name}
                                </option>
                            ))}
                        </select>
                        {errors.roleId && (
                            <p className="text-red-500 text-sm mt-1">{errors.roleId}</p>
                        )}
                        {isEditingSelf && (
                            <p className="text-amber-600 text-sm mt-1">
                                You cannot change your own role
                            </p>
                        )}
                    </div>

                    {/* Active Status */}
                    <div className="flex items-center">
                        <input
                            type="checkbox"
                            name="isActive"
                            checked={formData.isActive}
                            onChange={handleChange}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <label className="ml-2 block text-sm text-gray-900">
                            Active
                        </label>
                    </div>

                    {/* Camera Assignment Section - Only show when editing */}
                    {isEditing && (
                        <div className="border-t border-gray-200 pt-4">
                            <div className="flex items-center justify-between mb-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        Camera Access
                                    </label>
                                    {selectedCameras.length > 0 && (
                                        <p className="text-xs text-gray-500 mt-1">
                                            {selectedCameras.length} camera{selectedCameras.length !== 1 ? 's' : ''} currently assigned
                                        </p>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setShowCameraAssignment(!showCameraAssignment)}
                                    className="text-sm text-indigo-600 hover:text-indigo-800 flex items-center gap-1 px-3 py-1.5 rounded-md hover:bg-indigo-50 transition-colors"
                                >
                                    <Camera className="h-4 w-4" />
                                    {showCameraAssignment ? 'Hide' : 'Manage Cameras'}
                                </button>
                            </div>
                            
                            {showCameraAssignment && (
                                <div className="mt-4 space-y-4">
                                    {/* Tab Navigation */}
                                    <div className="flex border-b border-gray-200">
                                        <button
                                            type="button"
                                            onClick={() => setCameraAssignmentTab('individual')}
                                            className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                                                cameraAssignmentTab === 'individual'
                                                    ? 'text-indigo-600 border-b-2 border-indigo-600'
                                                    : 'text-gray-500 hover:text-gray-700'
                                            }`}
                                        >
                                            <Camera className="h-4 w-4 inline mr-2" />
                                            Individual Cameras ({selectedCameras.length})
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setCameraAssignmentTab('location')}
                                            className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                                                cameraAssignmentTab === 'location'
                                                    ? 'text-indigo-600 border-b-2 border-indigo-600'
                                                    : 'text-gray-500 hover:text-gray-700'
                                            }`}
                                        >
                                            <MapPin className="h-4 w-4 inline mr-2" />
                                            By Location ({selectedLocations.length})
                                        </button>
                                    </div>

                                    {/* Search */}
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                        <input
                                            type="text"
                                            placeholder={`Search ${cameraAssignmentTab === 'individual' ? 'cameras' : 'locations'}...`}
                                            value={cameraSearchTerm}
                                            onChange={(e) => setCameraSearchTerm(e.target.value)}
                                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                                        />
                                    </div>

                                    {/* Content Area */}
                                    <div className="max-h-80 overflow-y-auto border border-gray-200 rounded-lg p-3 bg-gray-50">
                                        {loadingCameras ? (
                                            <div className="flex items-center justify-center py-8">
                                                <Loader2 className="h-5 w-5 animate-spin text-indigo-600 mr-2" />
                                                <span className="text-gray-600">Loading cameras...</span>
                                            </div>
                                        ) : cameraAssignmentTab === 'individual' ? (
                                            <div className="space-y-2">
                                                {filteredCameras.length === 0 ? (
                                                    <div className="text-center py-8 text-gray-500">
                                                        <Camera className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                                                        <p>No cameras found</p>
                                                    </div>
                                                ) : (
                                                    filteredCameras.map(camera => {
                                                        const isSelected = selectedCameras.includes(camera._id)
                                                        return (
                                                            <div
                                                                key={camera._id}
                                                                onClick={() => handleCameraToggle(camera._id)}
                                                                className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${
                                                                    isSelected
                                                                        ? 'bg-indigo-50 border-indigo-300 shadow-sm'
                                                                        : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                                                                }`}
                                                            >
                                                                <div className="flex items-center space-x-3 flex-1 min-w-0">
                                                                    <div className={`w-4 h-4 rounded-full flex-shrink-0 border-2 flex items-center justify-center ${
                                                                        isSelected 
                                                                            ? 'bg-indigo-600 border-indigo-600' 
                                                                            : 'bg-white border-gray-300'
                                                                    }`}>
                                                                        {isSelected && (
                                                                            <div className="w-2 h-2 rounded-full bg-white"></div>
                                                                        )}
                                                                    </div>
                                                                    <div className="flex-1 min-w-0">
                                                                        <p className={`text-sm font-medium truncate ${
                                                                            isSelected ? 'text-indigo-900' : 'text-gray-900'
                                                                        }`}>{camera.name}</p>
                                                                        {camera.location?.exactLocation && (
                                                                            <p className="text-xs text-gray-500 truncate mt-0.5">
                                                                                {camera.location.exactLocation}
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
                                                                    {isSelected && (
                                                                        <span className="text-xs font-medium text-indigo-600">Assigned</span>
                                                                    )}
                                                                    <div className={`w-2.5 h-2.5 rounded-full ${
                                                                        camera.isActive ? 'bg-green-500' : 'bg-gray-400'
                                                                    }`} title={camera.isActive ? 'Active' : 'Inactive'}></div>
                                                                </div>
                                                            </div>
                                                        )
                                                    })
                                                )}
                                            </div>
                                        ) : (
                                            <div className="space-y-2">
                                                {locationOptions.filter(loc => 
                                                    loc.toLowerCase().includes(cameraSearchTerm.toLowerCase())
                                                ).length === 0 ? (
                                                    <div className="text-center py-8 text-gray-500">
                                                        <MapPin className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                                                        <p>No locations found</p>
                                                    </div>
                                                ) : (
                                                    locationOptions
                                                        .filter(loc => 
                                                            loc.toLowerCase().includes(cameraSearchTerm.toLowerCase())
                                                        )
                                                        .map(location => {
                                                            const camerasAtLocation = camerasByLocation[location] || []
                                                            const allSelected = camerasAtLocation.length > 0 && 
                                                                camerasAtLocation.every(cam => selectedCameras.includes(cam._id))
                                                            const someSelected = camerasAtLocation.some(cam => selectedCameras.includes(cam._id))
                                                            const selectedCount = camerasAtLocation.filter(cam => selectedCameras.includes(cam._id)).length
                                                            
                                                            // Location is considered selected if all cameras are selected
                                                            const isLocationSelected = allSelected
                                                            
                                                            return (
                                                                <div
                                                                    key={location}
                                                                    onClick={() => handleLocationToggle(location)}
                                                                    className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${
                                                                        isLocationSelected
                                                                            ? 'bg-indigo-50 border-indigo-300 shadow-sm'
                                                                            : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                                                                    }`}
                                                                >
                                                                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                                                                        <div className={`w-4 h-4 rounded-full flex-shrink-0 border-2 flex items-center justify-center ${
                                                                            isLocationSelected
                                                                                ? 'bg-indigo-600 border-indigo-600'
                                                                                : 'bg-white border-gray-300'
                                                                        }`}>
                                                                            {isLocationSelected && (
                                                                                <div className="w-2 h-2 rounded-full bg-white"></div>
                                                                            )}
                                                                        </div>
                                                                        <div className="flex-1 min-w-0">
                                                                            <p className={`text-sm font-medium ${
                                                                                isLocationSelected ? 'text-indigo-900' : 'text-gray-900'
                                                                            }`}>{location}</p>
                                                                            <p className="text-xs text-gray-500 mt-0.5">
                                                                                {camerasAtLocation.length} camera{camerasAtLocation.length !== 1 ? 's' : ''}
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                    <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
                                                                        {someSelected && !allSelected && (
                                                                            <span className="text-xs font-medium text-amber-600">
                                                                                {selectedCount}/{camerasAtLocation.length}
                                                                            </span>
                                                                        )}
                                                                        {allSelected && (
                                                                            <span className="text-xs font-medium text-indigo-600">All selected</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )
                                                        })
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex items-center justify-between text-xs pt-3 border-t border-gray-200">
                                        <div className="text-gray-600">
                                            {cameraAssignmentTab === 'individual' ? (
                                                <span><strong>{selectedCameras.length}</strong> camera{selectedCameras.length !== 1 ? 's' : ''} selected</span>
                                            ) : (
                                                <span><strong>{selectedLocations.length}</strong> location{selectedLocations.length !== 1 ? 's' : ''} selected</span>
                                            )}
                                        </div>
                                        {cameraAssignmentsModified && (
                                            <span className="text-amber-600 font-medium">• Unsaved changes</span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Form Actions */}
                    {isEditingSelf && (
                        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-4">
                            <p className="text-amber-800 text-sm">
                                <strong>Note:</strong> You cannot change your own role. Other profile information can be updated.
                            </p>
                        </div>
                    )}
                    <div className="flex space-x-3 pt-4 border-t border-gray-200 flex-shrink-0">
                        <button
                            type="button"
                            onClick={onClose}
                            className="btn btn-outline flex-1"
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary flex-1"
                            disabled={loading}
                        >
                            {loading ? (
                                <div className="flex items-center">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    Saving...
                                </div>
                            ) : (
                                <div className="flex items-center">
                                    <Save className="h-4 w-4 mr-2" />
                                    {isEditing ? 'Update' : 'Create'}
                                </div>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
}

export default UserModal 